// import { isEmpty } from 'lodash'
import { mutationNames } from '@/store/storeKey'
import { HashCodeFromInt, createStyleTag } from '@/modules/helper/index'
import config from '@/config'
import api from '@/modules/api'
import * as storage from '@/modules/storage'
import activityConfig from '@/activityConfig'

const state = {
  domainSettings: storage.domainSettings.get(),
  appThemes: storage.appThemes.get(),
  themesBackgroundColors: storage.themesBackgroundColors.get(),
  textureImages: storage.textureImages.get(),
  fonts: storage.fonts.get(),
  systemSubscriptionModules: storage.systemSubscriptionModules.get(),
  activityName: activityConfig
}

const getters = {
  domainSettings: state => state.domainSettings,
  appThemes: state => {
    return state.appThemes.map(theme => {
      return {
        ...theme,
        AppThemeColor: HashCodeFromInt(theme.AppThemeColor),
        AppThemeFontColor: HashCodeFromInt(theme.AppThemeFontColor),
        AppThemeSecondaryFontColor: HashCodeFromInt(theme.AppThemeSecondaryFontColor)
      }
    })
  },
  themesBackgroundColors: state => {
    return state.themesBackgroundColors.map(item => {
      return {
        ...item,
        BackgroundColor: HashCodeFromInt(item.BackgroundColor),
        BackgroundColorElements: HashCodeFromInt(item.BackgroundColorElements),
        BackgroundFontColor: HashCodeFromInt(item.BackgroundFontColor)
      }
    })
  },
  textureImages: state => state.textureImages,
  fonts: state => state.fonts,
  systemSubscriptionModules: state => state.systemSubscriptionModules,
  activityName: state => state.activityName
}

const mutations = {
  [mutationNames.SET_DOMAIN_SETTINGS] (state, domainSettings) {
    state.domainSettings = domainSettings
  },
  [mutationNames.SET_APP_THEMES] (state, appThemes) {
    state.appThemes = appThemes
  },
  [mutationNames.SET_THEMES_BACKGROUND_COLORS] (state, themesBackgroundColors) {
    state.themesBackgroundColors = themesBackgroundColors
  },
  [mutationNames.SET_TEXTURE_IMAGES] (state, textureImages) {
    state.textureImages = textureImages
  },
  [mutationNames.SET_FONTS] (state, fonts) {
    state.fonts = fonts
  },
  [mutationNames.SET_SYSTEM_SUBSCRIPTION_MODULES] (state, systemSubscriptionModules) {
    state.systemSubscriptionModules = systemSubscriptionModules
  }
}

const actions = {
  async populateDomainSettings ({ commit }) {
    const brandingParam = {
      DomainName: config.base_urls.DESIGNER_BASE_URL
    }
    const response = await api.system.getDomainSettings(brandingParam)
    const doaminSettings = {
      IdentityServer: `https://${response.IdentityServerURL}/api/AccountMethods/`,
      BackendServiceUrl: `https://${response.WebAPIURL}/api/`,
      AppsFTPURL: `https://${response.AppServerURL}/`,
      AppBuildingAPI: `https://${response.AppBuildingServiceURL}/api/`,
      AppThumbanilPath: '/assets/00-app-thumbnail.jpg',
      PrtocolUsed: 'https://',
      GoogleAppPath: '/native/binaries/android-app.apk',
      AppleAppPath: '/native/binaries/ios-app.ipa',
      TextureImagePath: `https://${response.AppServerURL}/.system/app_background_textures/thumbnail/`,
      AppTemplateImagePath: `https://${response.AppServerURL}/.system/app_template_backgrounds/`,
      InitialSubscriptionLevel_Id: response.InitialSubscriptionLevel_Id,
      InitialPurchasedSubscriptionLevel_Id: response.InitialPurchasedSubscriptionLevel_Id,
      BackgroundImageLogin: `https://${response.AppServerURL}/domains/${response.Domain}/${response.BackgroundImageLogin}`,
      BackgroundImageControlPanel: `https://${response.AppServerURL}/domains/${response.Domain}/${response.BackgroundImageControlPanel}`,
      LogoLarge: `https://${response.AppServerURL}/domains/${response.Domain}/${response.LogoLarge}`,
      LogoSmall: `https://${response.AppServerURL}/domains/${response.Domain}/${response.LogoSmall}`,
      WebAppPath: `/app/index.html`,
      FacebookGraphApiPath: `https://graph.facebook.com/`,
      ChatModuleUrl: `https://${response.ChatAPIURL}/api/`,
      IpstackUrl: 'http://api.ipstack.com/',
      IpApiUrl: 'http://ip-api.com/json/',
      ExtremelookupUrl: 'https://extreme-ip-lookup.com/json/',
      FreegeoiplookupUrl: 'https://freegeoip.net/json/'
    }
    storage.domainSettings.set(doaminSettings)
    commit(mutationNames.SET_DOMAIN_SETTINGS, doaminSettings)
  },

  async populateAppThemes ({ commit }) {
    const appThemes = await api.system.getAppThemes()
    storage.appThemes.set(appThemes)
    commit(mutationNames.SET_APP_THEMES, appThemes)
  },

  async populateThemesBackgroundColors ({ commit }) {
    const themesBackgroundColors = await api.system.getThemesBackgroundColors(storage.language.get())
    storage.themesBackgroundColors.set(themesBackgroundColors)
    commit(mutationNames.SET_THEMES_BACKGROUND_COLORS, themesBackgroundColors)
  },

  async populateTextureImages ({ commit }) {
    let textureImages = await api.system.getTextureImages()
    textureImages.unshift({ Id: 0, IsActive: false, Name: '' })
    storage.textureImages.set(textureImages)
    commit(mutationNames.SET_TEXTURE_IMAGES, textureImages)
  },

  async populateFonts ({ commit, getters }) {
    const fonts = await api.system.getAvailableFonts()
    storage.fonts.set(fonts)
    const fontFaceStyleString = fonts.reduce((accumulator, currentValue) => {
      return accumulator + `
        @font-face {
          font-family: "${currentValue.FontFileName}";
          src: url('/static/fonts/${currentValue.FontFileName}/${currentValue.FontFileName}.woff') format('woff');
        }`
    }, '')
    const qlFontFaceStyleString = fonts.reduce((accumulator, currentValue) => {
      return accumulator + `
        .ql-font-${currentValue.FontFileName} {
          font-family: '${currentValue.FontFileName}';
        }
        .ql-font span[data-value="${currentValue.FontFileName}"]::before {
          font-family: '${currentValue.FontFileName}';
        }`
    }, '')
    createStyleTag('system-table-fonts', fontFaceStyleString)
    createStyleTag('ql-fonts', qlFontFaceStyleString)
    // createStyleTag('SystemTableFont', fonts.reduce((accumulator, currentValue) => {
    //   return accumulator + `
    //     .ql-font-${currentValue.FontFileName} {
    //       font-family: '${currentValue.FontFileName}';
    //     }
    //     .ql-font span[data-value="${currentValue.FontFileName}"]::before {
    //       font-family: '${currentValue.FontFileName}';
    //     }
    //   `
    // }, ''))
    commit(mutationNames.SET_FONTS, fonts)
  },
  async populateSystemSubscriptionModule ({ commit, rootGetters }) {
    const modules = await api.system.getSubscriptionModules(rootGetters.language)
    storage.systemSubscriptionModules.set(modules)
    commit(mutationNames.SET_SYSTEM_SUBSCRIPTION_MODULES, modules)
  },

  logError ({ rootGetters }, payload) {
    return api.system.logError({
      ...payload,
      Client_Id: rootGetters.IsAuthenticated ? rootGetters.user.Id : null,
      Subscription_GUID: rootGetters.subscription ? rootGetters.subscription.Subscription_GUID : '',
      ErrorDate: new Date(),
      ErrorSource: 'DESIGNER',
      ViewName: window.location.pathname,
      UserAgent: navigator.userAgent
    })
  },
  activityLog ({ rootGetters }, payload) {
    return api.system.activityLog({
      ...payload,
      Client_Id: rootGetters.user.Id
      // Client_Id: rootGetters.IsAuthenticated ? rootGetters.user.Id : null
    })
  } }

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/system.js